import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTableUiTheme } from '../../contexts/TableUiThemeContext';
import { cn } from '../../lib/utils';
import {
  buildTableActionsColumn,
  countTableActionButtons,
} from '../../config/appConfig';
import {
  getTableActionButtonClass,
  getTableActionsConfig,
} from '../../config/tableActionButtons';
import {
  AdvancedTable,
  CardTable,
  BoardTable,
  TimelineTable,
  ModernTable,
  CardAdvancedTable
} from '../tables';
import { CategoryGroupedTable } from './CategoryGroupedTable';
import { EyeIcon, PencilIcon, TrashIcon, CopyIcon, CodeIcon } from '../ui/Icons';

/**
 * TableSection - Handles table rendering with different variants
 * Extracted from GenericFormsPage for better maintainability
 */
const TableSection = ({
  // Table configuration
  tableVariant,
  tableConfig,
  tableDensity,
  entityName,
  
  // Data
  currentPageData,
  
  // Columns
  getVisibleColumns,
  
  // Split view
  config,
  isSplitViewOpen,
  
  // Board configuration
  boardLookupState,
  
  // Event handlers
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  onFileCountClick,
  onTodoTaskCountClick,
  onTableConfigChange,
  onSplitViewSelect,
  
  // Labels
  labels
}) => {
  const { theme } = useTheme();
  const { themeId: tableUiThemeId } = useTableUiTheme();

  const showTooltips = getTableActionsConfig('showTooltips');
  const spacing = getTableActionsConfig('spacing');
  const actionsColumn = buildTableActionsColumn(config);
  const hasActionButtons = countTableActionButtons(config) > 0;
  const tableStorageId = String(
    config?.ui?.tableStorageKey || entityName || 'default',
  ).toLowerCase();

  // Action buttons renderer
  const renderActionButtons = (item) => {
    void tableUiThemeId;
    // Get button visibility configuration from module config or use defaults
    const showViewButton = config.ui?.showViewButton !== false; // Default to true
    const showEditButton = config.ui?.showEditButton !== false; // Default to true
    const canEditItem = config.canEdit ? config.canEdit(item) : true; // Check if item can be edited
    const showDeleteButton = config.ui?.showDeleteButton !== false; // Default to true
    const showDuplicateButton = config.ui?.showDuplicateButton === true; // Default to false (opt-in)
    const showCustomViewHtmlButton = !!config.customHandlers?.onViewHtml;
    
    // If all buttons are hidden, don't render actions column
    if (!showViewButton && !showEditButton && !showDeleteButton && !showDuplicateButton && !showCustomViewHtmlButton) {
      return null;
    }

    return (
      <div className={cn('flex items-center justify-center shrink-0', spacing)}>
      {showViewButton && (
        <button
          type="button"
          onClick={() => config.ui?.splitView ? onSplitViewSelect(item) : onView(item)}
          className={getTableActionButtonClass('view', tableDensity, theme)}
          title={showTooltips ? labels.viewTooltip : undefined}
        >
          <EyeIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
        </button>
      )}
      {showEditButton && canEditItem && (
        <button
          type="button"
          onClick={() => onEdit(item)}
          className={getTableActionButtonClass('edit', tableDensity, theme)}
          title={showTooltips ? labels.editTooltip : undefined}
        >
          <PencilIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
        </button>
      )}
      {showDuplicateButton && onDuplicate && (
        <button
          type="button"
          onClick={() => onDuplicate(item)}
          className={getTableActionButtonClass('duplicate', tableDensity, theme)}
          title={showTooltips ? (labels.duplicateTooltip || 'Duplicate') : undefined}
        >
          <CopyIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
        </button>
      )}
      {showDeleteButton && (() => {
        const canDelete = config.canDelete ? config.canDelete(item) : true;
        if (!canDelete) return null;

        return (
          <button
            type="button"
            onClick={() => onDelete(item)}
            className={getTableActionButtonClass('delete', tableDensity, theme)}
            title={showTooltips ? labels.deleteTooltip : undefined}
          >
            <TrashIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
          </button>
        );
      })()}
      {showCustomViewHtmlButton && (
        <button
          type="button"
          onClick={() => config.customHandlers.onViewHtml(item)}
          className={getTableActionButtonClass('html', tableDensity, theme)}
          title={showTooltips ? 'View HTML' : undefined}
        >
          <CodeIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
        </button>
      )}
     </div>
   );
   };

  // Compact action buttons for board/timeline
  const renderCompactActionButtons = (item) => {
    // Get button visibility configuration from module config or use defaults
    const showViewButton = config.ui?.showViewButton !== false; // Default to true
    const showEditButton = config.ui?.showEditButton !== false; // Default to true
    const canEditItem = config.canEdit ? config.canEdit(item) : true; // Check if item can be edited
    const showDeleteButton = config.ui?.showDeleteButton !== false; // Default to true
    const showDuplicateButton = config.ui?.showDuplicateButton === true; // Default to false (opt-in)
    const showCustomViewHtmlButton = !!config.customHandlers?.onViewHtml;
    
    return (
      <>
        {showViewButton && (
          <button
            type="button"
            onClick={() => config.ui?.splitView ? onSplitViewSelect(item) : onView(item)}
            className={getTableActionButtonClass('view', tableDensity, theme)}
            title={labels.viewTooltip}
          >
            <EyeIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
          </button>
        )}
        {showEditButton && canEditItem && (
          <button
            type="button"
            onClick={() => onEdit(item)}
            className={getTableActionButtonClass('edit', tableDensity, theme)}
            title={labels.editTooltip}
          >
            <PencilIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
          </button>
        )}
        {showDuplicateButton && onDuplicate && (
          <button
            type="button"
            onClick={() => onDuplicate(item)}
            className={getTableActionButtonClass('duplicate', tableDensity, theme)}
            title={labels.duplicateTooltip || 'Duplicate'}
          >
            <CopyIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
          </button>
        )}
        {showDeleteButton && (() => {
          const canDelete = config.canDelete ? config.canDelete(item) : true;
          if (!canDelete) return null;

          return (
            <button
              type="button"
              onClick={() => onDelete(item)}
              className={getTableActionButtonClass('delete', tableDensity, theme)}
              title={labels.deleteTooltip}
            >
              <TrashIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
            </button>
          );
        })()}
        {showCustomViewHtmlButton && (
          <button
            type="button"
            onClick={() => config.customHandlers.onViewHtml(item)}
            className={getTableActionButtonClass('html', tableDensity, theme)}
            title="View HTML"
          >
            <CodeIcon className={cn('advanced-action-icon', `density-${tableDensity}`)} />
          </button>
        )}
      </>
    );
  };

  const renderTable = () => {
    switch (tableVariant) {
      case 'cards':
        return (
          <CardTable
            columns={getVisibleColumns()}
            data={currentPageData}
            tableId={`${tableStorageId}-cards`}
            onRowClick={null}
            actionsRenderer={renderActionButtons}
            onFileCountClick={onFileCountClick}
          />
        );

      case 'board':
        return (
          <BoardTable
            columns={getVisibleColumns()}
            data={currentPageData}
            groupByKey={config.ui?.boardGroupByKey}
            groupLookup={boardLookupState}
            onCardDrop={({ item, fromGroup, toGroup }) => {
              if (typeof config.customHandlers?.onBoardDrop === 'function') {
                config.customHandlers.onBoardDrop({ item, fromGroup, toGroup });
              }
            }}
            onRowClick={null}
            actionsRenderer={renderCompactActionButtons}
            onFileCountClick={onFileCountClick}
          />
        );

      case 'timeline':
        return (
          <TimelineTable
            columns={getVisibleColumns()}
            data={currentPageData}
            startDateKey={config.ui?.timelineStartKey}
            endDateKey={config.ui?.timelineEndKey}
            dateScale={config.ui?.timelineScale || 'week'}
            onRowClick={null}
            actionsRenderer={renderCompactActionButtons}
            onFileCountClick={onFileCountClick}
          />
        );

      case 'categoryGroups': {
        const groupField = config.ui?.categoryGroupField || 'category';
        const formatLabel = config.ui?.formatCategoryLabel || ((key) => key);
        const listColumns = getVisibleColumns().filter(
          (c) => c.key !== groupField && c.key !== 'actions',
        );
        return (
          <CategoryGroupedTable
            data={currentPageData}
            columns={listColumns}
            groupByField={groupField}
            formatCategoryLabel={formatLabel}
            renderActions={hasActionButtons ? renderActionButtons : undefined}
            moduleConfig={config}
            tableDensity={tableDensity}
            className="min-h-0"
          />
        );
      }

      case 'modern':
        return (
          <ModernTable
            columns={
              hasActionButtons && actionsColumn
                ? [actionsColumn, ...getVisibleColumns()]
                : getVisibleColumns()
            }
            data={currentPageData.map(item => ({
              ...item,
              actions: renderActionButtons(item)
            }))}
            density={tableDensity}
            tableId={`${tableStorageId}-modern`}
            className="min-h-0"
            customSortOrders={config.customSortOrders}
            externalColumnWidths={tableConfig.columnWidths}
            externalColumnOrder={tableConfig.columnOrder}
            externalSortConfig={tableConfig.sortConfig}
            onConfigChange={onTableConfigChange}
            onFileCountClick={onFileCountClick}
            onTodoTaskCountClick={onTodoTaskCountClick}
            variant={tableVariant}
          />
        );

      case 'advancedCard':
        return (
          <CardAdvancedTable
            columns={
              hasActionButtons && actionsColumn
                ? [actionsColumn, ...getVisibleColumns()]
                : getVisibleColumns()
            }
            data={currentPageData.map(item => {
              const mappedItem = {
                ...item,
                actions: renderActionButtons(item)
              };
              //console.log('Mapped item for advancedCard:', mappedItem);
              return mappedItem;
            })}
            density={tableDensity}
            onDensityChange={(newDensity) => {
              // Handle density change if needed
              //console.log('Density changed to:', newDensity);
            }}
            tableId={`${tableStorageId}-advanced-card`}
            className="min-h-0"
            externalColumnWidths={tableConfig.columnWidths}
            externalColumnOrder={tableConfig.columnOrder}
            externalSortConfig={tableConfig.sortConfig}
            onConfigChange={onTableConfigChange}
            showHeader={true}
            showDensityControl={true}
            rowPositioning={config.ui?.rowPositioning || 'auto'}
            customRowOrder={config.ui?.customRowOrder || []}
            columnPositioning={config.ui?.columnPositioning || 'auto'}
          />
        );

      default: // 'default'
        return (
          <AdvancedTable
            columns={
              hasActionButtons && actionsColumn
                ? [actionsColumn, ...getVisibleColumns()]
                : getVisibleColumns()
            }
            data={currentPageData.map(item => ({
              ...item,
              ...(hasActionButtons && { actions: renderActionButtons(item) }),
            }))}
            density={tableDensity}
            tableId={tableStorageId}
            className="min-h-0"
            customSortOrders={config.customSortOrders}
            externalColumnWidths={tableConfig.columnWidths}
            externalColumnOrder={tableConfig.columnOrder}
            externalSortConfig={tableConfig.sortConfig}
            onConfigChange={onTableConfigChange}
            onFileCountClick={onFileCountClick}
            onTodoTaskCountClick={onTodoTaskCountClick}
            variant={tableVariant}
            moduleConfig={config}
          />
        );
    }
  };

  return (
    <div
      className={cn(
        'flex-1 min-h-0 min-w-0 w-full h-full px-2 pb-2 table-theme-classic overflow-hidden',
        tableVariant === 'categoryGroups' && 'flex flex-col pt-1',
      )}
    >
      {renderTable()}
    </div>
  );
};

export default TableSection;
